import { useEffect, useRef, useState } from "react";
import {
  formatAmountInput,
  formatDepositAmountInput,
  parseAmountInput,
  parseDepositAmountInput,
} from "@/client/money";
import {
  terminalDiagnosticCopy,
  terminalDiagnosticDetails,
} from "@/client/terminal-diagnostics";
import type {
  CatalogDraft,
  RefundSettings,
  TerminalDiagnostic,
  TerminalResult,
} from "@/client/types";
import { PlusIcon } from "@/components/icons";

type SettingsDialogProps = {
  open: boolean;
  settings: RefundSettings;
  installationId?: string;
  catalog: CatalogDraft[];
  terminalResult: TerminalResult;
  terminalDiagnostic: TerminalDiagnostic | null;
  onClose: () => void;
  onSave: (
    settings: RefundSettings,
    catalog: CatalogDraft[],
  ) => Promise<boolean>;
  onBootstrap: () => void;
  onTerminalTest: () => void;
};

const emptyDraft: CatalogDraft = {
  name: "",
  sku: "",
  itemId: "",
  depositCents: 25,
  enabled: true,
  sortOrder: 0,
};

type AmountInputProps = {
  "aria-label"?: string;
  cents: number;
  id?: string;
  maximumCents: number;
  negative?: boolean;
  onChange: (cents: number) => void;
};

function AmountInput({
  "aria-label": ariaLabel,
  cents,
  id,
  maximumCents,
  negative = false,
  onChange,
}: AmountInputProps) {
  const format = negative ? formatDepositAmountInput : formatAmountInput;
  const parse = negative ? parseDepositAmountInput : parseAmountInput;
  const [value, setValue] = useState(() => format(cents));
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) setValue(format(cents));
  }, [cents, format]);

  return (
    <input
      aria-label={ariaLabel}
      id={id}
      inputMode="decimal"
      value={value}
      onBlur={() => {
        focused.current = false;
        const parsed = parse(value, maximumCents);
        setValue(format(parsed ?? cents));
        if (parsed !== null) onChange(parsed);
      }}
      onChange={(event) => {
        const nextValue = event.target.value;
        setValue(nextValue);
        const parsed = parse(nextValue, maximumCents);
        if (parsed !== null) onChange(parsed);
      }}
      onFocus={() => {
        focused.current = true;
      }}
    />
  );
}

export function SettingsDialog({
  open,
  settings,
  installationId,
  catalog,
  terminalResult,
  terminalDiagnostic,
  onClose,
  onSave,
  onBootstrap,
  onTerminalTest,
}: SettingsDialogProps) {
  const [draft, setDraft] = useState(settings);
  const [draftCatalog, setDraftCatalog] = useState(catalog);
  const [item, setItem] = useState<CatalogDraft>(emptyDraft);
  const [saving, setSaving] = useState(false);
  const wasOpen = useRef(false);
  const diagnosticCopy = terminalDiagnostic
    ? terminalDiagnosticCopy(terminalDiagnostic)
    : null;

  useEffect(() => {
    if (open && !wasOpen.current) {
      setDraft(settings);
      setDraftCatalog(catalog);
      setItem(emptyDraft);
    }
    wasOpen.current = open;
  }, [open, settings, catalog]);

  if (!open) return null;

  function update<Key extends keyof RefundSettings>(
    key: Key,
    value: RefundSettings[Key],
  ) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function addItem() {
    if (
      !item.name.trim() ||
      !item.sku.trim() ||
      !item.itemId.trim() ||
      item.depositCents < 1
    )
      return;
    setDraftCatalog((current) => [
      ...current,
      {
        ...item,
        name: item.name.trim(),
        sku: item.sku.trim(),
        itemId: item.itemId.trim(),
        sortOrder: current.length,
      },
    ]);
    setItem(emptyDraft);
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <section
        aria-labelledby="settings-title"
        aria-modal="true"
        className="settings-dialog"
        role="dialog"
      >
        <header className="dialog-header">
          <button className="dialog-close" type="button" onClick={onClose}>
            Schließen
          </button>
          <h2 id="settings-title">Einstellungen</h2>
        </header>
        <div className="settings-content">
          <section className="settings-section">
            <h3>Lightspeed-Verbindung</h3>
            {!installationId && (
              <div className="bootstrap-row">
                <span>Diese Kasse ist noch nicht installiert.</span>
                <button
                  className="button button-secondary"
                  type="button"
                  onClick={onBootstrap}
                >
                  Installation erstellen
                </button>
              </div>
            )}
            {installationId && (
              <p className="installation-id">
                Installation: <code>{installationId}</code> · Zugangsdaten
                liegen im lokalen Lightspeed-Kassenspeicher.
              </p>
            )}
            <div className="settings-grid">
              <label>
                Umgebung
                <select
                  value={draft.environment}
                  onChange={(event) =>
                    update(
                      "environment",
                      event.target.value as RefundSettings["environment"],
                    )
                  }
                >
                  <option value="test">Test</option>
                  <option value="live">Live</option>
                </select>
              </label>
              <label>
                Region
                <select
                  value={draft.region}
                  onChange={(event) =>
                    update(
                      "region",
                      event.target.value as RefundSettings["region"],
                    )
                  }
                >
                  <option value="eu">Europa</option>
                  <option value="us">USA</option>
                  <option value="au">Australien</option>
                  <option value="apse">Asien-Pazifik</option>
                </select>
              </label>
              <label>
                Merchant Account
                <input
                  value={draft.merchantAccount}
                  onChange={(event) =>
                    update("merchantAccount", event.target.value)
                  }
                />
              </label>
              <label>
                Terminal ID
                <input
                  value={draft.terminalId}
                  onChange={(event) => update("terminalId", event.target.value)}
                />
              </label>
              <label>
                Sale ID
                <input
                  value={draft.saleId}
                  onChange={(event) => update("saleId", event.target.value)}
                />
              </label>
              <label>
                API-Schlüssel
                <input
                  autoComplete="off"
                  type="password"
                  value={draft.apiKey}
                  onChange={(event) => update("apiKey", event.target.value)}
                />
              </label>
              <label>
                Webhook-HMAC-Secret
                <input
                  autoComplete="off"
                  type="password"
                  value={draft.webhookHmacKey}
                  onChange={(event) =>
                    update("webhookHmacKey", event.target.value)
                  }
                />
              </label>
              <label>
                Webhook-URL
                <input readOnly value={draft.webhookUrl} />
              </label>
            </div>
            <div className="terminal-test">
              <div>
                <strong>Terminaltest</strong>
                <span>
                  {terminalResult === "testing"
                    ? "Verbindung wird geprüft …"
                    : terminalResult === "success"
                      ? "Terminal online"
                      : terminalResult === "failed"
                        ? (diagnosticCopy?.title ??
                          "Terminaltest fehlgeschlagen")
                        : "Status noch nicht geprüft"}
                </span>
              </div>
              <button
                className="button button-secondary"
                disabled={terminalResult === "testing"}
                type="button"
                onClick={onTerminalTest}
              >
                Terminal testen
              </button>
            </div>
            {terminalDiagnostic && diagnosticCopy && (
              <div
                className={`terminal-diagnostic ${
                  terminalDiagnostic.connected ? "success" : "warning"
                }`}
              >
                <strong>{diagnosticCopy.message}</strong>
                <p>{diagnosticCopy.action}</p>
                <details>
                  <summary>Technische Details</summary>
                  <ul>
                    {terminalDiagnosticDetails(terminalDiagnostic).map(
                      (detail) => (
                        <li key={detail}>{detail}</li>
                      ),
                    )}
                  </ul>
                </details>
              </div>
            )}
          </section>

          <section className="settings-section">
            <h3>Rückgabe-Regeln</h3>
            <div className="settings-grid three-columns">
              <label>
                Währung
                <select
                  value={draft.currency}
                  onChange={(event) =>
                    update(
                      "currency",
                      event.target.value as RefundSettings["currency"],
                    )
                  }
                >
                  <option value="EUR">EUR (€)</option>
                  <option value="USD">USD ($)</option>
                  <option value="AUD">AUD ($)</option>
                </select>
              </label>
              <label htmlFor="maximum-refund-amount">
                Maximum pro Rückgabe
                <AmountInput
                  cents={draft.maxRefundCents}
                  id="maximum-refund-amount"
                  maximumCents={100_000}
                  onChange={(cents) => update("maxRefundCents", cents)}
                />
              </label>
              <label>
                Finanzweg / Lightspeed Payment method ID
                <input
                  placeholder="z. B. PFAND-REFUND"
                  value={draft.settlementRoute}
                  onChange={(event) =>
                    update("settlementRoute", event.target.value)
                  }
                />
              </label>
            </div>
          </section>

          <section className="settings-section catalog-editor">
            <div>
              <h3>Pfandartikelkatalog</h3>
              <p>
                Pfandwerte werden einheitlich als negative Bonposition
                angezeigt. Der Lightspeed-Artikel muss mit demselben negativen
                Preis angelegt sein.
              </p>
            </div>
            <div className="catalog-form">
              <label>
                Artikelname
                <input
                  placeholder="z. B. Mehrwegflasche 0,5 l"
                  value={item.name}
                  onChange={(event) =>
                    setItem((current) => ({
                      ...current,
                      name: event.target.value,
                    }))
                  }
                />
              </label>
              <label>
                SKU
                <input
                  placeholder="PFAND-025"
                  value={item.sku}
                  onChange={(event) =>
                    setItem((current) => ({
                      ...current,
                      sku: event.target.value,
                    }))
                  }
                />
              </label>
              <label>
                Lightspeed itemId
                <input
                  placeholder="LS-Artikel-ID"
                  value={item.itemId}
                  onChange={(event) =>
                    setItem((current) => ({
                      ...current,
                      itemId: event.target.value,
                    }))
                  }
                />
              </label>
              <label htmlFor="new-deposit-amount">
                Pfandbetrag (€)
                <AmountInput
                  cents={item.depositCents}
                  id="new-deposit-amount"
                  maximumCents={10_000}
                  negative
                  onChange={(cents) =>
                    setItem((current) => ({
                      ...current,
                      depositCents: cents,
                    }))
                  }
                />
              </label>
              <button
                className="button button-secondary"
                type="button"
                onClick={addItem}
              >
                <PlusIcon /> Artikel anlegen
              </button>
            </div>
            <div className="catalog-list">
              {draftCatalog.map((entry, index) => (
                <div
                  className="catalog-edit-row"
                  key={`${entry.sku}-${entry.itemId}`}
                >
                  <input
                    aria-label="Artikelname"
                    value={entry.name}
                    onChange={(event) =>
                      setDraftCatalog(
                        draftCatalog.map((item, itemIndex) =>
                          itemIndex === index
                            ? { ...item, name: event.target.value }
                            : item,
                        ),
                      )
                    }
                  />
                  <input
                    aria-label="SKU"
                    value={entry.sku}
                    onChange={(event) =>
                      setDraftCatalog(
                        draftCatalog.map((item, itemIndex) =>
                          itemIndex === index
                            ? { ...item, sku: event.target.value }
                            : item,
                        ),
                      )
                    }
                  />
                  <input
                    aria-label="Lightspeed itemId"
                    value={entry.itemId}
                    onChange={(event) =>
                      setDraftCatalog(
                        draftCatalog.map((item, itemIndex) =>
                          itemIndex === index
                            ? { ...item, itemId: event.target.value }
                            : item,
                        ),
                      )
                    }
                  />
                  <AmountInput
                    aria-label={`Pfandbetrag ${entry.name}`}
                    cents={entry.depositCents}
                    maximumCents={10_000}
                    negative
                    onChange={(cents) =>
                      setDraftCatalog(
                        draftCatalog.map((item, itemIndex) =>
                          itemIndex === index
                            ? { ...item, depositCents: cents }
                            : item,
                        ),
                      )
                    }
                  />
                  <input
                    aria-label="Sortierung"
                    min="0"
                    type="number"
                    value={entry.sortOrder}
                    onChange={(event) =>
                      setDraftCatalog(
                        draftCatalog.map((item, itemIndex) =>
                          itemIndex === index
                            ? { ...item, sortOrder: Number(event.target.value) }
                            : item,
                        ),
                      )
                    }
                  />
                  <label className="enabled-toggle">
                    <input
                      checked={entry.enabled}
                      type="checkbox"
                      onChange={(event) =>
                        setDraftCatalog(
                          draftCatalog.map((item, itemIndex) =>
                            itemIndex === index
                              ? { ...item, enabled: event.target.checked }
                              : item,
                          ),
                        )
                      }
                    />{" "}
                    aktiv
                  </label>
                  <button
                    aria-label={`${entry.name} löschen`}
                    className="icon-button delete-button"
                    type="button"
                    onClick={() =>
                      setDraftCatalog(
                        draftCatalog.filter(
                          (_, itemIndex) => itemIndex !== index,
                        ),
                      )
                    }
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </section>
        </div>
        <footer className="dialog-footer">
          <button
            className="button button-ghost"
            type="button"
            onClick={onClose}
          >
            Abbrechen
          </button>
          <button
            className="button button-primary"
            disabled={!installationId || saving}
            type="button"
            onClick={() => {
              setSaving(true);
              void onSave(draft, draftCatalog).then((saved) => {
                setSaving(false);
                if (saved) onClose();
              });
            }}
          >
            {saving ? "Speichert …" : "Speichern"}
          </button>
        </footer>
      </section>
    </div>
  );
}
